import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@oniratec/onira-react-ui';
import { EntityHeaderConfig } from '../entity-header/types';
import { Link } from 'react-router';
import { ArrowLeft, ChevronDown, Settings2 } from 'lucide-react';

type Props = {
  config: EntityHeaderConfig;
};

const INLINE_ACTIONS_THRESHOLD = 1;

export function EntityActionHeader({ config }: Props) {
  const { title, backHref, status, reference, actions } = config;

  const visibleActions = actions?.filter(a => a.visible !== false) ?? [];
  const useDropdown = visibleActions.length > INLINE_ACTIONS_THRESHOLD;

  return (
    <div className="flex items-start justify-between gap-6 border-b p-4 bg-white">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          {backHref ? (
            <Link to={backHref} className="shrink-0 text-sm text-slate-500 hover:text-slate-700">
              <ArrowLeft className="size-5" />
            </Link>
          ) : null}
          <h1 className="min-w-0 text-lg font-semibold truncate lg:text-xl">
            {reference ?? title}
          </h1>
          {status}
        </div>
      </div>

      {visibleActions.length > 0 ? (
        <div className="flex items-center gap-2">
          {useDropdown ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <div className="flex flex-row items-center gap-2">
                    <Settings2 className="size-4" />
                    Acciones
                    <ChevronDown className="size-4" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={'z-0'}>
                {visibleActions.map(action =>
                  action.tooltip ? (
                    <Tooltip key={action.key}>
                      <TooltipTrigger asChild>
                        <span className="flex w-full">
                          <DropdownMenuItem
                            onClick={action.onClick}
                            disabled={action.disabled}
                            className={action.className}
                          >
                            <div className="flex flex-row items-center gap-2">
                              {action.icon && action.icon}
                              {action.label}
                            </div>
                          </DropdownMenuItem>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{action.tooltip}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <DropdownMenuItem
                      key={action.key}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={action.className}
                    >
                      <div className="flex flex-row items-center gap-2">
                        {action.icon && action.icon}
                        {action.label}
                      </div>
                    </DropdownMenuItem>
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            visibleActions.map(action => (
              <div className="flex flex-row" key={`section-${action.key}`}>
                {action.tooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button
                          key={action.key}
                          variant={action.variant ?? 'secondary'}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          loading={action.loading}
                          className={action.className && action.className}
                        >
                          <div className="flex flex-row items-center">
                            {action.icon && action.icon}
                            {action.label}
                          </div>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{action.tooltip}</TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    key={action.key}
                    variant={action.variant ?? 'secondary'}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    loading={action.loading}
                    className={action.className && action.className}
                  >
                    <div className="flex flex-row items-center">
                      {action.icon && action.icon}
                      {action.label}
                    </div>
                  </Button>
                )}
                {action.helper && action.helper}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
